import orderBy from 'lodash.orderby';
import keyBy from 'lodash.keyby';
import get from 'lodash.get';
import moment from 'moment';

// Local Types
import { QuantitySchedule, HarvestEvent } from './types';

/**
 * Calculates the duration in days
 */
export const getDuration = (
    startDate?: string,
    endDate?: string
): number | undefined => {
    if (!startDate || !endDate) {
        return undefined;
    }
    const momentStartDate = moment(startDate, 'YYYY-MM-DD');
    const momentEndDate = moment(endDate, 'YYYY-MM-DD');

    return momentEndDate.diff(momentStartDate, 'days') + 1;
};

/**
 * Finds the first date that a harvest event occurs on
 */
export const getHarvestEventStartDate = (
    harvestEvent: HarvestEvent
): string | undefined => {
    const { quantitySchedules } = harvestEvent;
    if (!quantitySchedules) {
        return undefined;
    }
    const orderedQuantitySchedules = orderBy(quantitySchedules, 'date');
    return get(orderedQuantitySchedules, [0, 'date']);
};

/**
 * Finds the last date that a harvest event occurs on
 */
export const getHarvestEventEndDate = (
    harvestEvent: HarvestEvent
): string | undefined => {
    const { quantitySchedules } = harvestEvent;
    if (!quantitySchedules) {
        return undefined;
    }
    const orderedQuantitySchedules = orderBy(quantitySchedules, 'date');
    return get(orderedQuantitySchedules, [
        quantitySchedules.length - 1,
        'date'
    ]);
};

/**
 * Creates empty quantity schedules for any days that are missing in between existing quantity schedules.
 */
export const formatQuantitySchedules = (
    harvestEvent: HarvestEvent
): QuantitySchedule[] => {
    const startDate = getHarvestEventStartDate(harvestEvent);
    const endDate = getHarvestEventEndDate(harvestEvent);

    // Find the duration of a harvest event based on the quantity schedules with the earliest and latest date
    const duration = getDuration(startDate, endDate);

    if (!duration) {
        return [];
    }

    const { quantitySchedules } = harvestEvent;
    const quantitySchedulesKeyedByDate = keyBy(quantitySchedules, 'date');
    const nextQuantitySchedules = [];

    // Loop through each day for the duration of a harvest event
    for (let index = 0; index < duration; index += 1) {
        const date = moment(startDate)
            .add(index, 'day')
            .format('YYYY-MM-DD');

        // Find an existing quantity schedule value for the date
        const existingQuantityScheduleValue = get(
            quantitySchedulesKeyedByDate,
            [date, 'value']
        );

        nextQuantitySchedules.push({
            date,
            value: existingQuantityScheduleValue || 0
        });
    }

    return nextQuantitySchedules;
};

const harvestEventColorGradeOptions = [
    {
        key: 'HIGH',
        text: 'High',
        value: 'HIGH'
    },
    {
        key: 'STANDARD',
        text: 'Standard',
        value: 'STANDARD'
    }
];

export const getHarvestEventColorGradeOptions = (): typeof harvestEventColorGradeOptions => {
    return harvestEventColorGradeOptions;
};
